import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api } from "@/helper/api";

export type RemainingManagerBookings =
  "checking" | "available" | "noWorkers" | "noSlots" | "error";

export function useRemainingManagerBookings(
  requestId: string,
  companySubjectId: string | number | null | undefined,
  enabled: boolean,
) {
  const [reload, setReload] = useState(0);
  const context = `${requestId}-${companySubjectId}-${enabled}-${reload}`;
  const [result, setResult] = useState<{
    context: string;
    state: RemainingManagerBookings;
  }>();

  useEffect(() => {
    if (!enabled) {
      setResult(undefined);
      return;
    }
    let disposed = false;
    const finish = (state: RemainingManagerBookings) => {
      if (!disposed) setResult({ context, state });
    };
    api.genericApiRequest({
      entity: "appointmentRequest",
      entityId: requestId,
      method: "get",
      additionalRouteParts: { booking_subjects: "/" },
      parametersToRender: { limit: 1, offset: 0 },
      successHandler: (response: { data: { results: unknown[] } }) => {
        if (disposed) return;
        if (!Array.isArray(response.data.results)) {
          finish("error");
          return;
        }
        if (!response.data.results.length) {
          finish("noWorkers");
          return;
        }
        api.genericApiRequest({
          entity: "appointmentRequest",
          entityId: requestId,
          method: "get",
          additionalRouteParts: { free_slots: "/" },
          parametersToRender: {
            depth: "0",
            additionalUrlParameters: {
              include_manager_booking_context: true,
              limit: 1,
            },
          },
          successHandler: (response: {
            data: { manager_booking_context?: { available_day_set: string[] } };
          }) => {
            const days =
              response.data.manager_booking_context?.available_day_set;
            if (!Array.isArray(days)) finish("error");
            else
              finish(
                days.some((day) => !dayjs(day).isBefore(dayjs(), "day"))
                  ? "available"
                  : "noSlots",
              );
          },
          failHandler: () => finish("error"),
        });
      },
      failHandler: () => finish("error"),
    });
    return () => {
      disposed = true;
    };
  }, [requestId, enabled, context]);

  const state: RemainingManagerBookings =
    result?.context === context ? result.state : "checking";
  return {
    state,
    retry: () => setReload((value) => value + 1),
  };
}
